import FontFaceObserver from 'fontfaceobserver';
import { Howl } from 'howler';
import { Assets } from 'pixi.js';
import ACTIVE_PLAYER_HALO_IMAGE_URL from '../assets/active-player-halo.png';
import * as CARD_URLS from '../assets/cards/cardAssetUrls';
import CARD_SUIT_CLUB_ICON_IMAGE_URL from '../assets/cards/suits/club-icon.png';
import CARD_SUIT_CLUB_ICON_FOUR_COLOR_IMAGE_URL from '../assets/cards/suits/club-icon-4color-deck.png';
import CARD_SUIT_DIAMOND_ICON_IMAGE_URL from '../assets/cards/suits/diamond-icon.png';
import CARD_SUIT_DIAMOND_ICON_FOUR_COLOR_IMAGE_URL from '../assets/cards/suits/diamond-icon-4color-deck.png';
import CARD_SUIT_HEART_ICON_IMAGE_URL from '../assets/cards/suits/heart-icon.png';
import CARD_SUIT_SPADE_ICON_IMAGE_URL from '../assets/cards/suits/spade-icon.png';
import DEALER_BUTTON_IMAGE_URL from '../assets/dealer-button.png';
import JACK_MENU_BUTTON_IMAGE_URL from '../assets/jack-menu-button.png';
import LOADING_SPINNER_IMAGE_URL from '../assets/loading-spinner.png';
import PLAYER_CHAT_BUBBLE_IMAGE_URL from '../assets/player-chat-bubble.png';
import SIDE_BET_ACTION_IMAGE_URL from '../assets/side-bet-action.png';
import { ALL_SOUND_ASSET_URLS } from '../assets/soundAssetUrls';
import { BACKGROUND_VIGNETTE_IMAGE_URL } from '../assets/themeBackgroundAssetUrls';
import { TABLE_RAIL_IMAGE_URL } from '../assets/themeTableAssetUrls';
import { ALL_FONT_FAMILIES } from '../constants/constants';
// SINGLETON ALERT!!!
let sharedLoaderPromise = null;
export const CARD_ASSET_URLS = {
    background: CARD_URLS.CARD_BACKGROUND_IMAGE_URL,
    back: CARD_URLS.CARD_BACK_IMAGE_URL,
    outline: CARD_URLS.CARD_OUTLINE_IMAGE_URL,
    brightBackground: CARD_URLS.CARD_BRIGHT_BACKGROUND_IMAGE_URL,
    darkBackground: CARD_URLS.CARD_DARK_BACKGROUND_IMAGE_URL,
    'rank-2': CARD_URLS.CARD_RANK_2_IMAGE_URL,
    'rank-3': CARD_URLS.CARD_RANK_3_IMAGE_URL,
    'rank-4': CARD_URLS.CARD_RANK_4_IMAGE_URL,
    'rank-5': CARD_URLS.CARD_RANK_5_IMAGE_URL,
    'rank-6': CARD_URLS.CARD_RANK_6_IMAGE_URL,
    'rank-7': CARD_URLS.CARD_RANK_7_IMAGE_URL,
    'rank-8': CARD_URLS.CARD_RANK_8_IMAGE_URL,
    'rank-9': CARD_URLS.CARD_RANK_9_IMAGE_URL,
    'rank-10': CARD_URLS.CARD_RANK_10_IMAGE_URL,
    'rank-J': CARD_URLS.CARD_RANK_J_IMAGE_URL,
    'rank-Q': CARD_URLS.CARD_RANK_Q_IMAGE_URL,
    'rank-K': CARD_URLS.CARD_RANK_K_IMAGE_URL,
    'rank-A': CARD_URLS.CARD_RANK_A_IMAGE_URL,
    'suit-club': CARD_URLS.CARD_SUIT_CLUB_IMAGE_URL,
    'suit-club-4color-deck': CARD_URLS.CARD_SUIT_CLUB_FOUR_COLOR_IMAGE_URL,
    'suit-diamond': CARD_URLS.CARD_SUIT_DIAMOND_IMAGE_URL,
    'suit-diamond-4color-deck': CARD_URLS.CARD_SUIT_DIAMOND_FOUR_COLOR_IMAGE_URL,
    'suit-heart': CARD_URLS.CARD_SUIT_HEART_IMAGE_URL,
    'suit-spade': CARD_URLS.CARD_SUIT_SPADE_IMAGE_URL,
    'club-icon': CARD_SUIT_CLUB_ICON_IMAGE_URL,
    'club-icon-4color-deck': CARD_SUIT_CLUB_ICON_FOUR_COLOR_IMAGE_URL,
    'diamond-icon': CARD_SUIT_DIAMOND_ICON_IMAGE_URL,
    'diamond-icon-4color-deck': CARD_SUIT_DIAMOND_ICON_FOUR_COLOR_IMAGE_URL,
    'heart-icon': CARD_SUIT_HEART_ICON_IMAGE_URL,
    'spade-icon': CARD_SUIT_SPADE_ICON_IMAGE_URL,
    'discard-overlay': CARD_URLS.CARD_DISCARD_OVERLAY_IMAGE_URL
};
export const IMAGE_ASSET_URLS = {
    ...CARD_ASSET_URLS,
    'active-player-halo': ACTIVE_PLAYER_HALO_IMAGE_URL,
    'dealer-button': DEALER_BUTTON_IMAGE_URL,
    'jack-menu-button': JACK_MENU_BUTTON_IMAGE_URL,
    'table-rail': TABLE_RAIL_IMAGE_URL,
    'player-chat-bubble': PLAYER_CHAT_BUBBLE_IMAGE_URL,
    'side-bet-action': SIDE_BET_ACTION_IMAGE_URL,
    'bg-vignette': BACKGROUND_VIGNETTE_IMAGE_URL,
    'loading-spinner': LOADING_SPINNER_IMAGE_URL
};
export async function loadAssets(loadedAssetCallback) {
    if (sharedLoaderPromise) {
        // Another app instance is assumed to have started (and maybe finished) loading assets via the shared loader,
        // so just return the existing singleton promise:
        return sharedLoaderPromise;
    }
    const startMs = performance.now();
    const assetsToLoad = {
        ...IMAGE_ASSET_URLS,
        'action-arrow': 'arrow.json'
    };
    let numLoaded = 0;
    const soundUrlsToPreload = Object.values(ALL_SOUND_ASSET_URLS);
    const numTotal = Object.keys(assetsToLoad).length + ALL_FONT_FAMILIES.length + soundUrlsToPreload.length;
    const assetsPromises = Object.entries(assetsToLoad).map(async ([alias, src]) => {
        const unresolvedAsset = { alias, src };
        await Assets.load(unresolvedAsset);
        numLoaded++;
        loadedAssetCallback(numLoaded, numTotal);
    });
    const pixiLoaderDonePromise = Promise.all(assetsPromises);
    const fontsLoaderDonePromise = new Promise((resolve) => {
        let numFontsLoaded = 0;
        ALL_FONT_FAMILIES.forEach(async (fontFamily) => {
            const timeoutMs = 10000;
            const font = new FontFaceObserver(fontFamily);
            await font.load(null, timeoutMs);
            console.log(`🔤 Font loaded: ${fontFamily}`);
            numLoaded++;
            numFontsLoaded++;
            loadedAssetCallback(numLoaded, numTotal);
            if (numFontsLoaded >= ALL_FONT_FAMILIES.length) {
                resolve();
            }
        });
    });
    const soundsLoaderDonePromise = new Promise((resolve) => {
        const numSoundsTotal = soundUrlsToPreload.length;
        let numSoundsLoaded = 0;
        function onLoad(soundUrl, isError = false) {
            if (!isError) {
                console.log(`🔈 Sound loaded: ${soundUrl}`);
            }
            numLoaded++;
            numSoundsLoaded++;
            loadedAssetCallback(numLoaded, numTotal);
            if (numSoundsLoaded >= numSoundsTotal) {
                resolve();
            }
        }
        function onLoadError(soundUrl) {
            console.error(`🔈 Sound load error: ${soundUrl}`);
            onLoad(soundUrl, true);
        }
        soundUrlsToPreload.forEach((soundUrl) => {
            new Howl({
                src: [soundUrl],
                onload: () => onLoad(soundUrl),
                onloaderror: () => onLoadError(soundUrl),
                preload: true,
                autoplay: false
            });
        });
    });
    sharedLoaderPromise = Promise.all([pixiLoaderDonePromise, fontsLoaderDonePromise, soundsLoaderDonePromise]).then(() => Promise.resolve());
    await sharedLoaderPromise;
    console.log(`Loading assets done, took ${(performance.now() - startMs).toLocaleString()}ms`);
}
